import { getLogEntries } from './ring-buffer';
import { buildMeta, buildEventsSnapshot } from './debug-snapshot';
import { buildAuthSnapshot } from './auth-snapshot';
import { defaultQueryClient } from '@shared/query-client/query-client';

/**
 * The one export function, reused by both surfaces (the devtools
 * "Canvasflow Debug" panel, for a developer live on a call, and the
 * About-dialog link, for a non-technical reader) --
 * improvements/17-debug-capture-export.md.
 * Nothing here transmits anything: a human triggers it, gets a file, and
 * chooses whether to send it. `fflate` is dynamically imported so it's
 * never in the main bundle for the ~100% of sessions that never call this.
 */
export async function exportDebugZip(): Promise<void> {
	const { zipSync, strToU8 } = await import('fflate');

	const [meta, logs, events, queries] = await Promise.all([
		Promise.resolve({ ...buildMeta(), exportedAt: new Date().toISOString() }),
		Promise.resolve(getLogEntries()),
		buildEventsSnapshot(),
		Promise.resolve(buildQueriesJson()),
	]);

	// `redacted: true` is the whole contract for this file: whitelisted
	// claims, unlisted claim *names* only, never a token string. The panel
	// calls the same builder with `redacted: false`.
	const auth = buildAuthSnapshot({ redacted: true });

	const files: Record<string, Uint8Array> = {
		'meta.json': strToU8(JSON.stringify(meta, null, 2)),
		'logs.json': strToU8(JSON.stringify(logs, errorReplacer, 2)),
		'events.json': strToU8(JSON.stringify(events, null, 2)),
		'queries.json': strToU8(JSON.stringify(queries, null, 2)),
	};

	// Omitted entirely rather than written as an empty object: an anonymous
	// reader's export shouldn't imply there was a session to inspect.
	if (auth.isAuthenticated) {
		files['auth.json'] = strToU8(JSON.stringify(auth, null, 2));
	}

	const zipped = zipSync(files);

	downloadZip(zipped, `debug-${meta.appKey}-${Date.now()}.zip`);
}

// Summary only -- key/status/error, never cached payloads. The zip is for
// diagnosing why something failed, not for re-exporting the product's
// actual content through a side channel.
function buildQueriesJson() {
	return defaultQueryClient
		.getQueryCache()
		.getAll()
		.map((query) => ({
			queryKey: query.queryKey,
			status: query.state.status,
			dataUpdatedAt: query.state.dataUpdatedAt,
			error:
				query.state.error instanceof Error
					? query.state.error.message
					: (query.state.error ?? null),
		}));
}

// A LogEntry's `message` can now carry raw arguments (see ring-buffer.ts),
// including bare Error objects -- JSON.stringify serializes those to `{}`
// by default (name/message/stack aren't enumerable own properties), so
// they'd otherwise vanish from the exported file entirely.
function errorReplacer(_key: string, value: unknown): unknown {
	if (value instanceof Error) {
		return { name: value.name, message: value.message, stack: value.stack };
	}
	return value;
}

function downloadZip(data: Uint8Array, filename: string): void {
	// `Blob`'s DOM types want a `Uint8Array<ArrayBuffer>` specifically (not
	// the wider `ArrayBufferLike` fflate's return type carries) -- fflate's
	// output is always backed by a real ArrayBuffer at runtime, never a
	// SharedArrayBuffer, so this narrows a type-level-only mismatch.
	const blob = new Blob([data as BlobPart], { type: 'application/zip' });
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement('a');
	anchor.href = url;
	anchor.download = filename;
	document.body.appendChild(anchor);
	anchor.click();
	anchor.remove();
	URL.revokeObjectURL(url);
}
